import React from 'react'
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull'
import CloudQueueIcon from '@mui/icons-material/CloudQueue'
import Co2Icon from '@mui/icons-material/Co2'
import EngineeringIcon from '@mui/icons-material/Engineering'
import GroupsIcon from '@mui/icons-material/Groups'
import HubIcon from '@mui/icons-material/Hub'
import InsightsIcon from '@mui/icons-material/Insights'
import MemoryIcon from '@mui/icons-material/Memory'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import SchoolIcon from '@mui/icons-material/School'
import SolarPowerIcon from '@mui/icons-material/SolarPower'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import VerifiedIcon from '@mui/icons-material/Verified'
import { PrimaryButton, SecondaryButton } from './Buttons'
import './LandingPageHome.css'

const valuePillars = [
  {
    icon: MemoryIcon,
    title: 'Intelligent Hardware',
    description: 'The AI-EMS Board optimizes solar, storage, and backup power in real time.',
  },
  {
    icon: SchoolIcon,
    title: 'Capacity Building',
    description: 'CNERGI Academy trains certified renewable energy installers and engineers.',
  },
  {
    icon: Co2Icon,
    title: 'Sustainability Monetization',
    description: 'Automated carbon reporting helps turn verified emissions reductions into revenue.',
  },
]

const services = [
  {
    icon: BatteryChargingFullIcon,
    title: 'AI-EMS Board',
    description: 'AI-powered hybrid energy storage management for solar integration.',
  },
  {
    icon: SchoolIcon,
    title: 'CNERGI Academy',
    description: 'Certification and skills training for installers, cooperatives, and engineers.',
  },
  {
    icon: CloudQueueIcon,
    title: 'SaaS Platform',
    description: 'Cloud monitoring, analytics, alerts, and white-label dashboards for partners.',
  },
  {
    icon: EngineeringIcon,
    title: 'Engineering & DIS',
    description: 'Feasibility studies, system design, quality engineering, and Distribution Impact Studies.',
  },
  {
    icon: SupportAgentIcon,
    title: 'After-Sales & AMC',
    description: 'Warranty support, remote diagnostics, field service, and maintenance contracts.',
  },
  {
    icon: TrendingUpIcon,
    title: 'Carbon Credits',
    description: 'Automated emissions reporting and carbon trading integration.',
  },
]

const reasonsForClients = [
  'DOST-validated technology',
  'Lower energy costs',
  'Carbon credit monetization',
  'Certified installer network',
  'End-to-end support',
]

const reasonsForInvestors = [
  'DOST-backed spinoff',
  'Multiple revenue streams',
  'Scalable SaaS model',
  'Government visibility programs',
  'First mover in carbon automation',
]

const storyMilestones = [
  {
    year: '2023-2024',
    event: 'AI-EMS R&D and DOST-CRADLE funding',
  },
  {
    year: '2025',
    event: 'Technology validation and prototyping',
  },
  {
    year: '2026',
    event: 'Spinoff incorporation (CNERGI Technologies, Inc.)',
  },
  {
    year: '2026',
    event: 'Commercial launch of AI-EMS Board and SaaS platform',
  },
]

const differentiators = [
  {
    title: 'DOST-Validated',
    description: 'Technology developed through rigorous government-funded R&D.',
  },
  {
    title: 'End-to-End Ecosystem',
    description: 'Hardware + training + software + carbon credits - all in one.',
  },
  {
    title: 'AI-Powered',
    description: 'Real-time optimization using artificial intelligence.',
  },
  {
    title: 'Localized',
    description: 'Built for Philippine conditions - islands, typhoons, remote areas.',
  },
  {
    title: 'Carbon-First',
    description: 'Monetize emissions reductions automatically.',
  },
  {
    title: 'Community-Focused',
    description: 'Training and empowering local installers.',
  },
]

const detailedSolutions = [
  {
    title: 'AI-EMS Board',
    tagline: 'The Brain of Your Energy System',
    description: 'The AI-EMS Board is an AI-driven hybrid energy management system that integrates solar PV, battery storage, and backup generators into one intelligent platform. It optimizes energy usage in real-time, reduces costs, and ensures reliable power - even in remote or off-grid locations.',
    features: [
      'Real-Time Monitoring',
      'AI-Powered Optimization',
      'Remote Firmware Updates',
      'Fault Detection',
      'Carbon Tracking',
    ],
    action: 'Inquire About AI-EMS Board',
  },
  {
    title: 'CNERGI Academy',
    tagline: 'Empowering the Next Generation of Installers',
    description: 'CNERGI Academy provides certification and skills training for electricians, engineers, cooperatives, and students. Our programs cover solar installation, AI-EMS Board maintenance, system design, and carbon credit documentation.',
    features: [
      'Solar + AI-EMS Board installation',
      'System design and optimization',
      'Carbon credit documentation',
      'Hands-on labs and on-site training',
    ],
    action: 'Enroll Now',
  },
  {
    title: 'SaaS Platform',
    tagline: 'Cloud Monitoring. White-Label Ready. Subscription-Based.',
    description: 'Our SaaS platform provides real-time cloud monitoring, analytics, remote firmware updates, and white-label dashboards for partner installers. Whether you are managing one site or a fleet of systems, CNERGI SaaS gives you complete visibility and control.',
    features: [
      'Live Dashboard',
      'Alerts & Notifications',
      'White-Label Ready',
      'API Access',
      'Carbon Reporting',
    ],
    action: 'Learn About SaaS',
  },
  {
    title: 'Engineering & DIS',
    tagline: 'Engineering Excellence. DIS Expertise.',
    description: 'CNERGI provides technical design, feasibility studies, system optimization, and quality assurance for renewable energy projects. Our Distribution Impact Study service evaluates the technical and economic effects of connecting distributed energy resources to the grid.',
    features: [
      'Site assessment and energy audits',
      'System sizing and simulation',
      'DIS - ERC-compliant',
      'Partnerships with DUs and ECs',
    ],
    action: 'Request Engineering Services',
  },
  {
    title: 'After-Sales & AMC',
    tagline: 'Reliable Support. Always.',
    description: 'CNERGI is committed to long-term partnership. Our after-sales services include warranty management, remote diagnostics, field service dispatch, spare parts supply, and Annual Maintenance Contracts.',
    features: [
      'Warranty management',
      'Remote diagnostics',
      'Field service dispatch',
      'Spare parts supply',
      'Annual Maintenance Contracts',
    ],
    action: 'Get Support',
  },
  {
    title: 'Partner Installer Network',
    tagline: 'Join the CNERGI Network',
    description: 'We partner with accredited third-party installers to deploy CNERGI AI-EMS Boards and related systems. As a partner installer, you get training, certification, lead referrals, technical hotline support, and supply of materials and equipment.',
    features: [
      'Training and certification',
      'Lead referral system',
      'Technical hotline',
      'Supply of materials and equipment',
    ],
    action: 'Become a Partner Installer',
  },
  {
    title: 'Carbon Credits Automation',
    tagline: 'Turn Emissions Reductions into Revenue',
    description: 'Our carbon credits automation platform automatically calculates, verifies, and packages carbon offsets from AI-EMS Board operations. From emissions reporting to trading, we make carbon monetization seamless.',
    features: [
      'Automated emissions reporting',
      'Integration with TIGRS Xpansiv',
      'White-label dashboard',
      'CNERGI Certified Carbon Neutral seal',
    ],
    action: 'Automate Your Carbon Credits',
  },
]

function LandingPageHome() {
  return (
    <main className="landing-home" id="home">
      <section className="landing-home__hero" aria-labelledby="home-title">
        <div className="landing-home__content">
          <p className="landing-home__eyebrow">CNERGI Technologies, Inc.</p>
          <h1 id="home-title">
            Intelligent Energy. <span>Visible Impact.</span>
          </h1>
          <p className="landing-home__lead">
            From the DOST-FASTRAC AI-EMS Project to your community, CNERGI delivers
            smart energy solutions, training, and carbon monetization in one seamless
            ecosystem.
          </p>

          <div className="landing-home__actions">
            <PrimaryButton href="#solutions">Explore Our Solutions</PrimaryButton>
            <SecondaryButton href="#contact">Partner With Us</SecondaryButton>
          </div>

          <div className="landing-home__features" aria-label="Featured capabilities">
            <div className="landing-home__feature">
              <InsightsIcon aria-hidden="true" />
              <span>Live monitoring</span>
            </div>
            <div className="landing-home__feature">
              <NotificationsActiveIcon aria-hidden="true" />
              <span>Fault alerts</span>
            </div>
            <div className="landing-home__feature">
              <MemoryIcon aria-hidden="true" />
              <span>AI optimization</span>
            </div>
            <div className="landing-home__feature">
              <SolarPowerIcon aria-hidden="true" />
              <span>Carbon tracking</span>
            </div>
          </div>
        </div>

      </section>

      <section className="landing-home__section" aria-labelledby="impact-title">
        <div className="landing-home__section-heading landing-home__section-heading--flow">
          <h2 id="impact-title">
            One Ecosystem. <span>Endless Impact.</span>
          </h2>
          <p>Hardware + Training + Software + Carbon Credits - all in one platform.</p>
        </div>

        <div className="landing-home__pillar-grid">
          {valuePillars.map(({ icon: Icon, title, description }) => (
            <article className="landing-home__pillar" key={title}>
              <Icon aria-hidden="true" />
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-home__section landing-home__section--alt" id="solutions" aria-labelledby="solutions-title">
        <div className="landing-home__section-heading">
          <p className="landing-home__eyebrow">What We Do</p>
          <h2 id="solutions-title">
            Smart energy services built for <span>Philippine deployment.</span>
          </h2>
        </div>

        <div className="landing-home__service-grid">
          {services.map(({ icon: Icon, title, description }) => (
            <article className="landing-home__service" key={title}>
              <Icon aria-hidden="true" />
              <div>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-home__trust" id="about" aria-labelledby="trust-title">
        <div className="landing-home__trust-content">
          <p className="landing-home__eyebrow">DOST-CRADLE Spinoff</p>
          <h2 id="trust-title">
            Science-backed. DOST-validated. <span>Community-ready.</span>
          </h2>
          <p>
            CNERGI Technologies, Inc. is a spinoff from the DOST-CRADLE funded
            AI-EMS Project under Project FASTRAC. Years of Philippine
            government-backed research and development have shaped technology
            that is ready for commercial buildings, off-grid communities, and
            renewable energy partners.
          </p>
        </div>

        <div className="landing-home__trust-media">
          <div className="landing-home__trust-badges" aria-label="Technology validation references">
            <span><VerifiedIcon aria-hidden="true" /> DOST-validated R&amp;D</span>
            <span><HubIcon aria-hidden="true" /> Project FASTRAC</span>
            <span><GroupsIcon aria-hidden="true" /> HAU TBI-KITTO incubation</span>
          </div>
        </div>
      </section>

      <section className="landing-home__section" aria-labelledby="why-title">
        <div className="landing-home__section-heading">
          <p className="landing-home__eyebrow">Why CNERGI</p>
          <h2 id="why-title">
            Tangible proof for clients and a <span>scalable model for investors.</span>
          </h2>
        </div>

        <div className="landing-home__reason-grid">
          <article className="landing-home__reason-panel">
            <h3>For Clients</h3>
            <ul>
              {reasonsForClients.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </article>
          <article className="landing-home__reason-panel">
            <h3>For Investors</h3>
            <ul>
              {reasonsForInvestors.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className="landing-home__about" aria-labelledby="about-title">
        <div className="landing-home__about-header">
          <p className="landing-home__eyebrow">About CNERGI Technologies, Inc.</p>
          <h2 id="about-title">
            From DOST-CRADLE to your community - <span>building a carbon-neutral Philippines.</span>
          </h2>
        </div>

        <div className="landing-home__about-layout">
          <div className="landing-home__about-copy">
            <section aria-labelledby="mission-title">
              <h3 id="mission-title">Mission &amp; Vision</h3>
              <p>
                <strong>Our Mission:</strong> To democratize clean energy intelligence through
                training, technology, and carbon monetization.
              </p>
              <p>
                <strong>Our Vision:</strong> A carbon-neutral Philippines powered by
                CNERGI-enabled ecosystems.
              </p>
            </section>

            <section aria-labelledby="story-title">
              <h3 id="story-title">Our Story</h3>
              <p>
                CNERGI Technologies, Inc. was born from the <strong>DOST-CRADLE funded
                AI-EMS Project</strong> under <strong>Project FASTRAC</strong>. The AI-EMS
                project was a pioneering research initiative aimed at developing intelligent
                energy management solutions for the Philippines.
              </p>
              <p>
                Recognizing the potential to transform research into real-world impact,
                the project team established CNERGI Technologies, Inc. - a technology
                spinoff dedicated to bringing AI-driven energy solutions to commercial
                buildings, off-grid communities, and renewable energy integrators.
              </p>
              <p>
                Today, CNERGI stands at the intersection of <strong>AI technology,
                renewable energy, and sustainability monetization</strong> - delivering
                tangible proof that intelligent energy is not just possible, but
                profitable and accessible.
              </p>
            </section>
          </div>

        </div>

        <div className="landing-home__timeline" aria-label="CNERGI story timeline">
          {storyMilestones.map(({ year, event }) => (
            <article className="landing-home__timeline-card" key={`${year}-${event}`}>
              <strong>{year}</strong>
              <span>{event}</span>
            </article>
          ))}
        </div>

        <section className="landing-home__differentiators" aria-labelledby="differentiators-title">
          <h3 id="differentiators-title">
            What Sets <span>Us Apart</span>
          </h3>
          <div className="landing-home__differentiator-grid">
            {differentiators.map(({ title, description }) => (
              <article className="landing-home__differentiator-card" key={title}>
                <h4>{title}</h4>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>
      </section>

      <section className="landing-home__solution-detail" aria-labelledby="solution-detail-title">
        <div className="landing-home__solution-detail-header">
          <p className="landing-home__eyebrow">Our Solutions</p>
          <h2 id="solution-detail-title">
            Intelligent Energy. <span>Delivered.</span>
          </h2>
        </div>

        <div className="landing-home__solution-stack">
          {detailedSolutions.map((solution) => (
            <article
              className="landing-home__solution-row"
              key={solution.title}
            >
              <div className="landing-home__solution-copy">
                <h3>{solution.title}</h3>
                <p className="landing-home__solution-tagline">{solution.tagline}</p>
                <p>{solution.description}</p>
                <ul>
                  {solution.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <PrimaryButton href="#contact">{solution.action}</PrimaryButton>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-home__contact" id="contact" aria-labelledby="contact-title">
        <div className="landing-home__contact-heading">
          <p className="landing-home__eyebrow">Contact</p>
          <h2 id="contact-title">
            Send Us a <span>Message</span>
          </h2>
        </div>

        <div className="landing-home__contact-layout">
          <form className="landing-home__contact-form">
            <label>
              Full Name
              <input type="text" name="fullName" placeholder="Juan Dela Cruz" />
            </label>
            <label>
              Email Address
              <input type="email" name="email" placeholder="juan@example.com" />
            </label>
            <label>
              Phone Number
              <input type="tel" name="phone" placeholder="0917 123 4567" />
            </label>
            <label>
              Organization / Company
              <input type="text" name="organization" placeholder="Your Company Name" />
            </label>
            <label>
              Subject
              <select name="subject" defaultValue="">
                <option value="" disabled>Select a subject...</option>
                <option>Inquire about AI-EMS Board</option>
                <option>Partner Installer Application</option>
                <option>CNERGI Academy Enrollment</option>
                <option>Engineering / DIS Services</option>
                <option>Carbon Credits Automation</option>
                <option>Press / Media Inquiry</option>
                <option>General Inquiry</option>
              </select>
            </label>
            <label>
              Message
              <textarea name="message" placeholder="Tell us how we can help..." rows="5"></textarea>
            </label>
            <label>
              How did you hear about us?
              <select name="source" defaultValue="">
                <option value="" disabled>Select an option...</option>
                <option>Referral</option>
                <option>Social Media</option>
                <option>DOST / Government Program</option>
                <option>Event or Conference</option>
                <option>Search Engine</option>
              </select>
            </label>
            <PrimaryButton>Send Message</PrimaryButton>
          </form>

          <div className="landing-home__contact-info">
            <article className="landing-home__contact-card">
              <h3>Get in Touch</h3>
              <p>
                Whether you&apos;re a Distribution Utility, Electric Cooperative,
                installer, developer, or investor - we&apos;d love to hear from you.
                CNERGI is open to partnerships that accelerate clean energy adoption
                and carbon monetization.
              </p>

              <div className="landing-home__contact-list">
                <div>
                  <span aria-hidden="true">📍</span>
                  <p><strong>Office Address</strong>CNERGI Technologies, Inc.<br />1F Plaza de Corazon, Flora Avenue<br />Santo Rosario, Angeles City, Pampanga</p>
                </div>
                <div>
                  <span aria-hidden="true">📞</span>
                  <p><strong>Phone</strong>[Insert Phone Number]</p>
                </div>
                <div>
                  <span aria-hidden="true">✉️</span>
                  <p><strong>Email</strong>[Insert Email Address]</p>
                </div>
                <div>
                  <span aria-hidden="true">🌐</span>
                  <p><strong>Website</strong>[Insert URL]</p>
                </div>
              </div>

              <div className="landing-home__social-links" aria-label="Social links">
                <strong>Follow Us:</strong>
                <a href="#facebook">Facebook</a>
                <a href="#linkedin">LinkedIn</a>
                <a href="#youtube">YouTube</a>
              </div>
            </article>

            <article className="landing-home__partnership-card">
              <h3>Interested in Partnering with CNERGI?</h3>
              <p>
                Whether you&apos;re a Distribution Utility, Electric Cooperative,
                installer, developer, or investor - we&apos;d love to hear from you.
              </p>
              <SecondaryButton href="mailto:marketing@cnergi.tech">Let&apos;s Talk Partnership</SecondaryButton>
            </article>

            <div className="landing-home__map" aria-label="OpenStreetMap location for CNERGI Technologies, Inc.">
              <iframe
                title="CNERGI Technologies, Inc. location map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=120.5700%2C15.1310%2C120.6010%2C15.1580&layer=mapnik&marker=15.1445%2C120.5855"
                loading="lazy"
              ></iframe>
              <a
                href="https://www.openstreetmap.org/?mlat=15.1445&mlon=120.5855#map=16/15.1445/120.5855"
                target="_blank"
                rel="noreferrer"
              >
                View larger map
              </a>
            </div>
          </div>
        </div>

        <article className="landing-home__press-card">
          <h3>Press &amp; Media Inquiries</h3>
          <p>For media inquiries, interviews, or press kits, please contact our Business Development Officer.</p>
          <strong>[Insert Media Contact Email]</strong>
        </article>
      </section>
    </main>
  )
}

export default LandingPageHome
